import type { Quote } from '@client/model';

/**
 * Parses a percentage string into a decimal number (e.g., "3%" -> 0.03, "5% Compound" -> 0.05)
 * Returns 0 for 'None' or invalid values
 */
export function parseInflationPercent(value?: string): number {
  if (!value || value === 'None') return 0;

  const match = value.match(/(\d+(\.\d+)?)/);
  const parsed = match ? parseFloat(match[1]) : NaN;

  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Parses a percentage string into a rate (e.g., "3%" -> 3, "5% Compound" -> 5)
 * Returns 0 for 'None' or invalid values
 */
export function parseInflationRate(label: string): number {
  if (label === 'None') return 0;
  const match = label.match(/(\d+(\.\d+)?)/);
  return match ? parseFloat(match[1]) : 0;
}

/**
 * Gets the inflation rate from a quote, checking multiple fields in priority order:
 * 1. aobInflation (if valid number)
 * 2. inflationBenefitOption
 * Returns the rate as a whole number (e.g., 3 for 3%)
 */
export function getInflationRateFromQuote(quote: Quote): number {
  if (quote.aobInflation && !isNaN(parseFloat(quote.aobInflation))) {
    return parseFloat(quote.aobInflation);
  }

  const inflationOption = quote.inflationBenefitOption;
  if (!inflationOption || inflationOption === 'None') {
    return 0;
  }

  const match = inflationOption?.match(/(\d+(\.\d+)?)/);
  if (match && match[0]) {
    return parseFloat(match[0]);
  }

  return 0;
}

/**
 * Gets the initial inflation label from a quote
 * Checks inflationBenefitOption, aobInflation, and cobInflation in order
 */
export function getInitialInflationLabel(quote: Quote): string {
  if (quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None') {
    return quote.inflationBenefitOption;
  }

  if (quote.aobInflation && quote.aobInflation !== 'None') {
    return quote.aobInflation;
  }

  if (quote.cobInflation && quote.cobInflation !== 'None') {
    return quote.cobInflation;
  }

  return 'None';
}

/**
 * Gets the inflation protection years from a quote
 * Looks for "for X years" pattern in inflationBenefitOption
 * Falls back to 20 years if aob/cob inflation exists but no specific duration found
 */
export function getInflationProtectionYears(quote: Quote): number | null {
  if (quote.inflationBenefitOption) {
    const yearsMatch = quote.inflationBenefitOption.match(/for\s+(\d+)\s+years?/i);
    if (yearsMatch && yearsMatch[1]) {
      return parseInt(yearsMatch[1], 10);
    }
  }

  if (
    !quote.cobInflation ||
    quote.cobInflation === '0' ||
    !quote.aobInflation ||
    quote.aobInflation === '0'
  ) {
    return null;
  }

  return 20;
}

/**
 * Calculates policy inflation protection based on inflation rate and quote fields
 * Returns null if inflation rate is 0 or if the calculation doesn't apply
 */
export function calculatePolicyInflationProtection(
  inflationRate: number,
  aobInflation: string | undefined,
  cobInflation: string | undefined,
): number | null {
  if (inflationRate <= 0) {
    return null;
  }

  const aobInflationParsed = aobInflation ? parseFloat(aobInflation) : undefined;
  const cobInflationParsed = cobInflation ? parseFloat(cobInflation) : undefined;

  if (aobInflationParsed !== undefined && cobInflationParsed !== undefined) {
    return aobInflationParsed !== cobInflationParsed ? inflationRate / 100 : null;
  }

  if (aobInflationParsed === undefined && cobInflationParsed === undefined) {
    return inflationRate / 100;
  }

  return null;
}

